

import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, fromEvent, merge, Subject } from 'rxjs';
import { auditTime, distinctUntilChanged, filter, map, mapTo, switchMap, takeUntil } from 'rxjs/operators';

import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { POSITION_MAP } from 'mwc/core/overlay';
import { BooleanInput, IndexableObject } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import { MwcDropdownMenuComponent, MwcPlacementType } from './dropdown-menu.component';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'dropDown';

const listOfPositions = [
  POSITION_MAP.bottomLeft,
  POSITION_MAP.bottomRight,
  POSITION_MAP.topRight,
  POSITION_MAP.topLeft
];

@Directive({
  selector: '[mwc-dropdown]',
  exportAs: 'mwcDropdown',
  host: {
    class: 'mwc-dropdown-trigger'
  }
})
export class MwcDropDownDirective implements AfterViewInit, OnDestroy, OnChanges {
  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;

  static ngAcceptInputType_mwcBackdrop: BooleanInput;
  static ngAcceptInputType_mwcClickHide: BooleanInput;
  static ngAcceptInputType_mwcDisabled: BooleanInput;
  static ngAcceptInputType_mwcVisible: BooleanInput;

  private portal?: TemplatePortal;
  private overlayRef: OverlayRef | null = null;
  private destroy$ = new Subject();
  private positionStrategy = this.overlay
    .position()
    .flexibleConnectedTo(this.elementRef.nativeElement)
    .withLockedPosition()
    .withTransformOriginOn('.mwc-dropdown');
  private inputVisible$ = new BehaviorSubject<boolean>(false);
  private mwcTrigger$ = new BehaviorSubject<'click' | 'hover'>('hover');
  private overlayClose$ = new Subject<boolean>();
  @Input() mwcDropdownMenu: MwcDropdownMenuComponent | null = null;
  @Input() mwcTrigger: 'click' | 'hover' = 'hover';
  @Input() mwcMatchWidthElement: ElementRef | null = null;
  @Input() @WithConfig<boolean>() @InputBoolean() mwcBackdrop = false;
  @Input() @InputBoolean() mwcClickHide = true;
  @Input() @InputBoolean() mwcDisabled = false;
  @Input() @InputBoolean() mwcVisible = false;
  @Input() mwcOverlayClassName: string = '';
  @Input() mwcOverlayStyle: IndexableObject = {};
  @Input() mwcPlacement: MwcPlacementType = 'bottomLeft';
  @Output() readonly mwcVisibleChange: EventEmitter<boolean> = new EventEmitter();

  setDropdownMenuValue<T extends keyof MwcDropdownMenuComponent>(key: T, value: MwcDropdownMenuComponent[T]): void {
    if (this.mwcDropdownMenu) {
      this.mwcDropdownMenu.setValue(key, value);
    }
  }

  constructor(
    public readonly mwcConfigService: MwcConfigService,
    public elementRef: ElementRef,
    private overlay: Overlay,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private platform: Platform
  ) {}

  ngAfterViewInit(): void {
    if (this.mwcDropdownMenu) {
      const nativeElement: HTMLElement = this.elementRef.nativeElement;
      /** host mouse state **/
      const hostMouseState$ = merge(
        fromEvent(nativeElement, 'mouseenter').pipe(mapTo(true)),
        fromEvent(nativeElement, 'mouseleave').pipe(mapTo(false))
      );
      /** menu mouse state **/
      const menuMouseState$ = this.mwcDropdownMenu.mouseState$;
      /** merged mouse state **/
      const mergedMouseState$ = merge(menuMouseState$, hostMouseState$);
      /** host click state **/
      const hostClickState$ = fromEvent(nativeElement, 'click').pipe(map(() => !this.mwcVisible));
      /** visible state switch by mwcTrigger **/
      const visibleStateByTrigger$ = this.mwcTrigger$.pipe(
        switchMap(trigger => {
          if (trigger === 'hover') {
            return mergedMouseState$;
          } else if (trigger === 'click') {
            return hostClickState$;
          } else {
            return EMPTY;
          }
        })
      );
      const descendantMenuItemClick$ = this.mwcDropdownMenu.descendantMenuItemClick$.pipe(
        filter(() => this.mwcClickHide),
        mapTo(false)
      );
      const domTriggerVisible$ = merge(visibleStateByTrigger$, descendantMenuItemClick$, this.overlayClose$).pipe(
        filter(() => !this.mwcDisabled)
      );
      const visible$ = merge(this.inputVisible$, domTriggerVisible$);
      combineLatest([visible$, this.mwcDropdownMenu.isChildSubMenuOpen$])
        .pipe(
          map(([visible, sub]) => visible || sub),
          auditTime(150),
          distinctUntilChanged(),
          filter(() => this.platform.isBrowser),
          takeUntil(this.destroy$)
        )
        .subscribe((visible: boolean) => {
          const element = this.mwcMatchWidthElement ? this.mwcMatchWidthElement.nativeElement : nativeElement;
          const triggerWidth = element.getBoundingClientRect().width;
          if (this.mwcVisible !== visible) {
            this.mwcVisibleChange.emit(visible);
          }
          this.mwcVisible = visible;
          if (visible) {
            /** set up overlayRef **/
            if (!this.overlayRef) {
              /** new overlay **/
              this.overlayRef = this.overlay.create({
                positionStrategy: this.positionStrategy,
                minWidth: triggerWidth,
                disposeOnNavigation: true,
                hasBackdrop: this.mwcBackdrop && this.mwcTrigger === 'click',
                scrollStrategy: this.overlay.scrollStrategies.reposition()
              });
              merge(
                this.overlayRef.backdropClick(),
                this.overlayRef.detachments(),
                this.overlayRef
                  .outsidePointerEvents()
                  .pipe(filter((e: MouseEvent) => !this.elementRef.nativeElement.contains(e.target))),
                this.overlayRef.keydownEvents().pipe(filter(e => e.keyCode === ESCAPE && !hasModifierKey(e)))
              )
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  this.overlayClose$.next(false);
                });
            } else {
              /** update overlay config **/
              const overlayConfig = this.overlayRef.getConfig();
              overlayConfig.minWidth = triggerWidth;
            }
            /** open dropdown with animation **/
            this.positionStrategy.withPositions([POSITION_MAP[this.mwcPlacement], ...listOfPositions]);
            /** reset portal if needed **/
            if (!this.portal || this.portal.templateRef !== this.mwcDropdownMenu!.templateRef) {
              this.portal = new TemplatePortal(this.mwcDropdownMenu!.templateRef, this.viewContainerRef);
            }
            this.overlayRef.attach(this.portal);
          } else {
            /** detach overlayRef if needed **/
            if (this.overlayRef) {
              this.overlayRef.detach();
            }
          }
        });

      this.mwcDropdownMenu!.animationStateChange$.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event.toState === 'void') {
          if (this.overlayRef) {
            this.overlayRef.dispose();
          }
          this.overlayRef = null;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcVisible, mwcDisabled, mwcOverlayClassName, mwcOverlayStyle, mwcTrigger } = changes;
    if (mwcTrigger) {
      this.mwcTrigger$.next(this.mwcTrigger);
    }
    if (mwcVisible) {
      this.inputVisible$.next(this.mwcVisible);
    }
    if (mwcDisabled) {
      const nativeElement = this.elementRef.nativeElement;
      if (this.mwcDisabled) {
        this.renderer.setAttribute(nativeElement, 'disabled', '');
        this.inputVisible$.next(false);
      } else {
        this.renderer.removeAttribute(nativeElement, 'disabled');
      }
    }
    if (mwcOverlayClassName) {
      this.setDropdownMenuValue('mwcOverlayClassName', this.mwcOverlayClassName);
    }
    if (mwcOverlayStyle) {
      this.setDropdownMenuValue('mwcOverlayStyle', this.mwcOverlayStyle);
    }
  }
}
